import { Link } from "react-router-dom";
import { Star, BadgeCheck, Loader2 } from "lucide-react";
import { mediaUrl } from "../lib/api";

export function formatNum(n) {
  n = Number(n || 0);
  if (n >= 1e9) return (n / 1e9).toFixed(1).replace(/\.0$/, "") + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
  return String(n);
}

export function vc(n) { return Number(n || 0).toFixed(2); }

export function formatDuration(sec) {
  sec = Math.floor(Number(sec || 0));
  if (sec <= 0) return "";
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  const pad = (x) => String(x).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

const _MENTION_SPLIT = /(@[A-Za-z0-9_.]{3,30})/g;
export function renderMentions(text) {
  if (!text) return text;
  const parts = String(text).split(_MENTION_SPLIT);
  return parts.map((part, i) => {
    if (/^@[A-Za-z0-9_.]{3,30}$/.test(part)) {
      const handle = part.slice(1);
      return <Link key={i} to={`/${handle}`} className="text-eblue hover:underline" data-testid={`mention-${handle}`}>{part}</Link>;
    }
    return part;
  });
}

export function Avatar({ src, name, size = 40, ring = false, className = "" }) {
  const letter = (name || "?").charAt(0).toUpperCase();
  return (
    <div className={`relative shrink-0 rounded-full overflow-hidden ${ring ? "ring-2 ring-[color:var(--eblue)]" : ""} ${className}`}
      style={{ width: size, height: size }}>
      {src ? (
        <img src={mediaUrl(src)} alt={name} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1a2238] to-[#0b1021] text-silver font-display font-bold"
          style={{ fontSize: size * 0.42 }}>{letter}</div>
      )}
    </div>
  );
}

export function SimBadge({ className = "" }) {
  return <span className={`inline-flex items-center text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/25 ${className}`} title="Simulated demo content — not a real user">SIM</span>;
}

export function CreatorName({ user, className = "", showAt = false }) {
  if (!user) return null;
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <span className="truncate">{user.display_name}</span>
      {user.plan === "plus" && <Star size={13} className="text-gold fill-[var(--gold)]" />}
      {user.verified && <BadgeCheck size={14} className="text-eblue" />}
      {user.is_demo && <SimBadge />}
      {showAt && <span className="text-muted-foreground font-normal">@{user.username}</span>}
    </span>
  );
}

export function CreatorLink({ user, size = 40, sub, right }) {
  if (!user) return null;
  return (
    <div className="flex items-center gap-3 min-w-0">
      <Link to={`/${user.username}`} data-testid={`creator-link-${user.username}`}>
        <Avatar src={user.avatar_url} name={user.display_name} size={size} />
      </Link>
      <div className="min-w-0 flex-1">
        <Link to={`/${user.username}`} className="font-semibold text-sm hover:text-eblue transition-colors block truncate">
          <CreatorName user={user} />
        </Link>
        <div className="text-xs text-muted-foreground truncate">{sub || `@${user.username}`}</div>
      </div>
      {right}
    </div>
  );
}

export function Spinner({ className = "" }) {
  return <Loader2 className={`animate-spin ${className}`} />;
}

export function Loading({ label = "Loading the industry…" }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
      <Spinner className="w-7 h-7 text-eblue" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function Empty({ icon: Icon, title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center px-6">
      {Icon && <div className="w-16 h-16 rounded-2xl surface-2 flex items-center justify-center mb-4"><Icon className="w-7 h-7 text-muted-foreground" /></div>}
      <div className="font-display font-semibold text-lg">{title}</div>
      {hint && <div className="text-sm text-muted-foreground mt-1 max-w-sm">{hint}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Skeleton({ className = "" }) {
  return <div className={`animate-pulse bg-white/5 rounded-lg ${className}`} />;
}

export function VScore({ value, size = "sm" }) {
  const cls = size === "lg" ? "text-2xl" : "text-sm";
  return (
    <span className={`font-mono text-gold ${cls} tabular`}>{Number(value || 0).toFixed(0)}</span>
  );
}

export function SectionHead({ title, sub, action }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-5">
      <div>
        <h2 className="font-display font-bold text-xl md:text-2xl">{title}</h2>
        {sub && <p className="text-sm text-muted-foreground mt-0.5">{sub}</p>}
      </div>
      {action}
    </div>
  );
}
